/**
 * Coalesces streamed text deltas into at most one state update per
 * animation frame. A long answer arrives as hundreds of tiny
 * ``TEXT_MESSAGE_CONTENT`` events; applying each one re-renders the
 * whole transcript, so we buffer them and hand ``apply`` the joined
 * text once per frame instead.
 *
 * Callers must ``flush()`` before applying any other event (so state
 * changes stay in stream order) and when the stream ends or errors (so
 * no text is lost). ``cancel()`` drops buffered text — used on abort,
 * where the run is being discarded and a late write would leak stale
 * text into the next run.
 */
export interface DeltaBatcher {
  push: (delta: string) => void;
  flush: () => void;
  cancel: () => void;
}

export type FrameScheduler = {
  request: (cb: () => void) => number;
  cancel: (handle: number) => void;
};

const FALLBACK_FRAME_MS = 16;

export function defaultFrameScheduler(): FrameScheduler {
  if (typeof requestAnimationFrame === "function") {
    return {
      request: (cb) => requestAnimationFrame(cb),
      cancel: (handle) => cancelAnimationFrame(handle),
    };
  }
  return {
    request: (cb) => setTimeout(cb, FALLBACK_FRAME_MS) as unknown as number,
    cancel: (handle) => clearTimeout(handle),
  };
}

export function createDeltaBatcher(
  apply: (text: string) => void,
  scheduler: FrameScheduler = defaultFrameScheduler(),
): DeltaBatcher {
  let pending = "";
  let handle: number | null = null;

  const clearScheduled = () => {
    if (handle !== null) {
      scheduler.cancel(handle);
      handle = null;
    }
  };

  const flush = () => {
    clearScheduled();
    if (!pending) return;
    const text = pending;
    pending = "";
    apply(text);
  };

  return {
    push(delta) {
      if (!delta) return;
      pending += delta;
      if (handle === null) {
        handle = scheduler.request(() => {
          handle = null;
          flush();
        });
      }
    },
    flush,
    cancel() {
      clearScheduled();
      pending = "";
    },
  };
}
